import React from 'react';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { money } from './totals';
import { DocBox, AddRowButton, DOC_FIELD } from './DocumentChrome';

/**
 * Plate charges and die charges — one component, two boxes.
 *
 * These are one-time charges the customer pays on top of the printing, and they
 * are taxed with it, so every row's amount lands in the same taxable base as the
 * line items. Both tables are the same three columns; only the heading colour
 * and the wording of the last column differ between the two documents.
 */
export default function ChargesTable({
  heading, tone, rows, infoLabel, particularsPlaceholder, infoPlaceholder,
  onChange, onAdd, onRemove, name,
}) {
  const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  return (
    <DocBox
      heading={heading}
      tone={tone}
      actions={
        <span className="font-mono text-[11px] font-semibold tabular-nums" data-testid={`${name}-total`}>
          ₹ {money(total)}
        </span>
      }
    >
      <table className="w-full border-collapse" data-testid={`${name}-table`}>
        <thead>
          <tr className="bg-slate-100/80 text-[9px] uppercase tracking-[0.08em] text-slate-500">
            <th scope="col" className="w-[42%] px-2 py-1 text-left">Particulars</th>
            <th scope="col" className="w-[24%] px-2 py-1 text-right">Amount (₹)</th>
            <th scope="col" className="px-2 py-1 text-left">{infoLabel}</th>
            <th scope="col" className="w-8 px-1 py-1" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-2 py-3 text-center text-[11px] text-slate-400">No charges</td>
            </tr>
          ) : rows.map((row, index) => (
            <tr key={row.id} className="border-b border-slate-100" data-testid={`${name}-row-${index}`}>
              <td className="px-1">
                <Input
                  value={row.particulars ?? ''}
                  onChange={(e) => onChange(row.id, { particulars: e.target.value })}
                  placeholder={particularsPlaceholder}
                  aria-label={`${heading} row ${index + 1} particulars`}
                  className={DOC_FIELD}
                />
              </td>
              <td className="px-1">
                <Input
                  type="number"
                  value={row.amount ?? ''}
                  onChange={(e) => onChange(row.id, { amount: e.target.value })}
                  placeholder="0.00"
                  aria-label={`${heading} row ${index + 1} amount`}
                  data-testid={`${name}-amount-${index}`}
                  className={`${DOC_FIELD} text-right font-mono tabular-nums`}
                />
              </td>
              <td className="px-1">
                <Input
                  value={row.info ?? ''}
                  onChange={(e) => onChange(row.id, { info: e.target.value })}
                  placeholder={infoPlaceholder}
                  aria-label={`${heading} row ${index + 1} ${infoLabel}`}
                  className={DOC_FIELD}
                />
              </td>
              <td className="px-1 text-center">
                <button
                  type="button"
                  onClick={() => onRemove(row.id)}
                  aria-label={`Delete ${heading} row ${index + 1}`}
                  data-testid={`delete-${name}-${index}`}
                  className="rounded p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-2 py-1.5">
        <AddRowButton onClick={onAdd} testId={`add-${name}`}>+ Add Row</AddRowButton>
      </div>
    </DocBox>
  );
}
